import { useCallback, useEffect } from 'react';

import { useTracking } from '@skyscanner/strevda-tracking';
import { useAdvertVisibilityTracking } from '@web-platform/shared-banana-redux/src/helpers/hooks/useAdvertVisibilityTracking';
import { CONFIG_SERVICE_KEYS } from '@web-platform/shared-universal-constants-config';
import { ANALYTICS_EVENTS } from '@web-platform/shared-universal-constants-tracking';
import { getUserDevice } from '@web-platform/shared-universal-redux/src/state';
import { getStrevdaTrackingConfig } from '@web-platform/shared-universal-tracking/src/getStrevdaTrackingConfig';
import { trackInteractionEvent } from '@web-platform/shared-universal-tracking/src/trackInteractionEvent/trackInteractionEvent';
import { translate } from '@web-platform/shared-universal-translations';
import { getConfigServiceValue } from '@web-platform/shared-universal-utils-frontend/src/config';
import { logWarn } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { compressGraphicPromoUrl } from '@web-platform/shared-universal-utils-universal/src/adverts/adverts';
import { useSelector } from 'react-redux';
import { redirect } from 'saddlebag-browser';

import { HomepageHero as HomepageHeroComponent } from './HomepageHero';

import type { HomepageHeroProps } from './HomepageHero';
import type { HomepageHero } from '@web-platform/shared-universal-types/src/redux/banana/state/combinedResults/entitiesSearchResults/adverts/homepageHero';

export type Props = {
  advert: HomepageHero;
};
export const HomepageHeroCard = ({ advert }: Props) => {
  const { ref: trackingRef, trackClick } = useTracking(
    advert,
    {},
    false,
    getStrevdaTrackingConfig(),
  );
  const { ref: visibilityTrackerRef } = useAdvertVisibilityTracking();
  const combinedRef = useCallback(
    (el: HTMLDivElement) => {
      visibilityTrackerRef.current = el;
      trackingRef(el);
    },
    [trackingRef, visibilityTrackerRef],
  );

  const { analyticsProperties, creative } = advert;
  const { isMobilePhone } = useSelector(getUserDevice);
  const enableCompressGraphicPromo = getConfigServiceValue(
    CONFIG_SERVICE_KEYS.OPS.ENABLE_COMPRESS_GRAPHIC_PROMO_MOBILE,
  );

  useEffect(() => {
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_AD_LOADED.NAME,
      strings: {
        creativeId: analyticsProperties.idCreative,
      },
    });
  }, [analyticsProperties.idCreative]);

  const onClick = useCallback(() => {
    trackClick();
    trackInteractionEvent({
      name: ANALYTICS_EVENTS.INTERACTION.HOMEPAGE_AD_TAPPED.NAME,
      strings: {
        creativeId: analyticsProperties.idCreative,
      },
    });

    redirect(creative.ctaUrl);
  }, [analyticsProperties.idCreative, creative.ctaUrl, trackClick]);

  if (!creative.partner) {
    logWarn({
      component: 'HomepageHero',
      message: 'Merchandising Homepage Heroes are not yet supported.',
      data: JSON.stringify(creative),
    });
    return null;
  }

  const mobileImageUrl =
    (isMobilePhone && enableCompressGraphicPromo
      ? compressGraphicPromoUrl(creative.backgroundImageMobile)
      : creative.backgroundImageMobile) ?? creative.backgroundImageDesktop;

  const style = {
    '--background-image-desktop': `url(${creative.backgroundImageDesktop})`,
    '--background-image-mobile': `url(${mobileImageUrl})`,
    ...(creative.fontColour && { color: creative.fontColour }),
    ...(creative.backgroundColour && { backgroundColor: creative.backgroundColour }),
  };
  const homepageHeroProps: HomepageHeroProps = {
    contentId: `homepage-hero__${analyticsProperties.idPlacement}`,
    headline: creative.headline,
    subheading: translate('homepageHero_in_partnership_with', {
      partner: creative.partner.name,
    }),
    logo: creative.partner.logo,
    altText: creative.backgroundImageAltText,
    buttonText: creative.buttonText,
    onClick,
    style,
    fontColor: style.color,
    ctaBackgroundColour: creative.ctaBackgroundColour,
    ctaFontColour: creative.ctaFontColour,
    pioPartnerName: creative.pioPartnerName,
    pioAgencyName: creative.pioAgencyName,
  };

  return (
    <div ref={combinedRef}>
      <HomepageHeroComponent {...homepageHeroProps} />
    </div>
  );
};
